import React from "react";
import { Section } from "../../../components/section";
import { SectionHeader } from "../../../components/section-header";
import { ImageTextTriplets } from "../../../components/image-text-triplets";
import { SolutionHeader } from "../../../components/solution-header";
import { FullWidthImage } from "../../../components/full-width-image";
import onboarding from "../../../images/Design_Onboarding.png";
import notifications from "../../../images/Design_Notifications.png";
import trust from "../../../images/Icon_Trust.png";
import personalization from "../../../images/Icon_Personalization.png";
import level from "../../../images/Icon_Level.png";
import { ScreenGroup } from "../../../components/screen-group";
import { Insight } from "../../../components/insight";

export interface IConsolidationProps {
  sectionId: string;
  index: number;
}

export function Adoption(props: IConsolidationProps) {
  const formattedIndex = props.index > 10 ? props.index : `0${props.index}`;
  return (
    <Section sectionId={props.sectionId} isLast={false}>
      <SectionHeader title={`${formattedIndex} Adoption`} />

      <SolutionHeader
        title="Onboarding"
        description="When testing earlier iterations, we inserted users into the interventions with no context to avoid biasing them before the test began. However, we found that trust and understanding were barriers to the effectiveness of the intervention because users had no ability to understand the features. In reality, a social platform would always announce new features of this scale to users to help them get comfortable with the changes. To simulate this, we created onboarding modules for both the Fact-Checker and the Engagement Hub."
      />

      <FullWidthImage imgSrc={onboarding} />

      <Insight
        insight={{
          body:
            "After creating the onboarding modules, 100% of our participants trusted the information provided in the fact-checker upon first encountering it.",
        }}
        index={0}
      />

      <ScreenGroup
        title="Notifications"
        description="With all these features, we needed to find a way to motivate users to use them on a regular basis. Here, we turned our focus to designing and testing our notification strategy. We designed and A/B tested different notifications around our main features with 250 users to surface which versions best motivated users to enter the Engagement Hub."
        features={[
          {
            title: "Successful Motivation",
            body:
              "Through testing, we were able to identify what elements made notifications effective in the context of our project. Of the four types of content we tested, Accounts to Review was the most successful, with 88% of our survey participants choosing to interact with the notification and take action. However, only 46% of users chose to take action when shown notifications related to Corrections. ",
            imgSrc: notifications,
          },
        ]}
      />

      <SolutionHeader
        title="The Three Ingredients of Effective Notifications"
        description="After analyzing our test results, we saw that notifications have potential to be catalysts for behavioral change—but they require the right ingredients in order to be effective for users. Here is what we recommend in the context of our solution:"
      />

      <ImageTextTriplets
        children={[
          {
            imgSrc: trust,
            title: "Trust",
            body:
              "In order to trust the notification enough to interact with it, users needed a certain level of control, such as in the timing and delivery method of the notification.",
          },
          {
            imgSrc: personalization,
            title: "Personalization",
            body:
              "Notifications need to be tailored to users' interests for users to be willing to act on them.",
          },
          {
            imgSrc: level,
            title: "Level of Detail",
            body:
              "Notifications should provide just enough detail to spark the user's curiosity and allow them to understand the purpose of the notification without overloading them with too much information.",
          },
        ]}
      />
    </Section>
  );
}
